<template>
  <div v-if="competition" class="competition-rounds">
    <div class="header-row">
      <h3>{{ $t("rounds.title") }}</h3>
      <div class="header-btn" v-if="isOwnerOrganizer(user, competition) && !this.competition.finished_at">
        <div class="publish-action-group">
          <template v-if="isOnlineCompetition">
            <button
                class="btn btn-warning"
                :disabled="!isPublishTimeReached || currentPhasePublished || currentPhaseHasPendingFeedbackTwos || currentPhaseHasRoundsBeingEdited || currentPhaseHasRoundsUnderAthleteReview"
                @click="publishStage"
            >
              <span v-if="!isPublishTimeReached">
                {{ $t("rounds.available") }} {{ getCountdownFormatted() }}
              </span>
              <span v-else>
                {{ $t("rounds.publish_stage") + phaseName }}
                <i class="fas fa-bullhorn"></i>
              </span>
            </button>
          </template>
          <template v-else>
            <button
                class="btn btn-warning"
                :disabled="!canPublishType2"
                @click="publishStage"
            >
              <span v-if="showType2Countdown">
                {{ $t("rounds.available") }} {{ getCountdownFormatted() }}
              </span>
              <span v-else>
                {{ $t("rounds.publish_stage") + phaseName }}
                <i class="fas fa-bullhorn"></i>
              </span>
            </button>
          </template>
          <ul v-if="publishBlockReasons.length" class="publish-block-reasons">
            <li
              v-for="reason in publishBlockReasons"
              :key="reason"
              class="publish-block-reasons__item"
            >
              {{ reason }}
            </li>
          </ul>
        </div>
        <button
            class="btn btn-primary advance-stage-btn"
            :disabled="!canAdvanceStage"
            @click="advanceStage"
        >
          {{ $t("rounds.advance_stage") + nextPhaseName }}
          <i class="fas fa-angles-right icon-arrow"></i>
        </button>
      </div>
    </div>

    <div class="round-buttons stage-tab-row">
      <button
          v-for="tab in qualificationTabs"
          :key="tab.key"
          :class="['round-toggle', 'stage-tab-button', `stage-tab-button--${tab.variant || 'qualification'}`, { active: selectedRound === tab.value }]"
          @click="selectRound(tab.value)"
      >
        <span class="stage-tab-button__copy">
          <span class="truncate-text stage-tab-button__title">{{ tab.label }}</span>
          <span :class="['stage-tab-button__status', `stage-tab-button__status--${tab.status}`]">
            {{ phaseStatusLabel(tab.status) }}
          </span>
        </span>
      </button>
    </div>

    <div v-if="stageTabs.length" class="stage-buttons stage-tab-row">
      <button
          v-for="tab in stageTabs"
          :key="tab.key"
          @click="selectStage(tab.value)"
          :class="['round-toggle', 'stage-toggle', 'stage-tab-button', 'stage-tab-button--stage', { active: selectedStage === tab.value }]"
      >
        <span class="stage-tab-button__copy">
          <span class="truncate-text stage-tab-button__title">{{ tab.label }}</span>
          <span class="stage-tab-button__meta">
            <span :class="['stage-tab-button__status', `stage-tab-button__status--${tab.status}`]">
              {{ phaseStatusLabel(tab.status) }}
            </span>
            <span class="stage-tab-button__count">{{ tab.count }}</span>
          </span>
        </span>
      </button>
    </div>
    <template v-if="competitionPodiums.length > 0">
      <button
          @click="selectPodium()"
          :class="['round-toggle', 'podium-toggle', 'stage-tab-button', 'stage-tab-button--podium', { active: selectedPodium } ]"
      >
        <span class="stage-tab-button__copy">
          <span class="truncate-text stage-tab-button__title">Podium</span>
        </span>
      </button>
    </template>

    <p
        v-if="selectedRound !== null || selectedStage !== null || selectedPodium !== null"
        class="section-label"
    >
      {{ $t('rounds.selected_section') }}
    </p>
    <div v-if="selectedRound !== null" ref="sectionContainer">
      <h4 v-if="selectedRound !== 'best'">
        {{ $t("rounds.qualifying") }} {{ selectedRound }}
      </h4>
      <h4 v-else>{{ $t("rounds.best_result") }}</h4>

      <StageTimersBox
        v-if="isOnlineCompetition && selectedRoundCalendar"
        :calendar="selectedRoundCalendar"
        :competition="competition"
        :code="selectedRoundCode"
        :now-ts="nowTs"
      />

      <CompetitionTable
        :competition="competition"
        :rounds="tableRounds"
        :advancing-count="qualificationAdvancingCount"
        :selectedStage="selectedStage"
        :selectedRound="selectedRound === null ? '' : selectedRound"
        @view-round="viewRound"
        @review-round="reviewRound"
      />

    </div>

    <div v-if="selectedStage !== null" ref="sectionContainer">
      <h4>{{ getStageName(selectedStage) }}</h4>
      <StageTimersBox
        v-if="isOnlineCompetition && selectedStageCalendar"
        :calendar="selectedStageCalendar"
        :competition="competition"
        :code="selectedStageCode"
        :now-ts="nowTs"
      />
      <div v-if="competition.is_vs">
        <CompetitionBracket :matches="getBracketMatches(selectedStage)" :selectedStage="selectedStage" :selectedRound="selectedRound" :roundSizes="competition.round_sizes" @view-round="viewRound" />
      </div>
      <div v-else>
        <CompetitionTable :competition="competition" :rounds="getStageAthletes(selectedStage)" :advancing-count="stageAdvancingCount" :selectedStage="selectedStage" :selectedRound="selectedRound"  @view-round="viewRound" @review-round="reviewRound" />
      </div>
    </div>

    <div v-if="selectedPodium !== null" ref="sectionContainer">
      <h4>{{ $t('rounds.podium') }}</h4>
      <CompetitionPodium :podiums="competitionPodiums" />
    </div>
  </div>
</template>

<script>
import CompetitionTable from "@/components/competitions/CompetitionTable.vue";
import CompetitionBracket from "@/components/competitions/CompetitionBracket.vue";
import CompetitionPodium from "@/components/competitions/CompetitionPodium.vue";
import StageTimersBox from "@/components/competitions/StageTimersBox.vue";
import { parseCompetitionDate, resolveCompetitionTimezone } from "@/helpers/timezoneDate";
import { assembleVsBracketMatches } from "@/helpers/vsBracketAssembly";
import echoService from "@/services/echoService";
import {mapActions, mapState} from 'vuex';
import {isOwnerOrganizer, isInnerAthlete} from '@/helpers/roleUtils';

export default {
  name: "CompetitionQualificationRounds",
  emits: ['advance-stage','publish-stage','view-round','review-round'],
  components: { CompetitionTable, CompetitionBracket,CompetitionPodium, StageTimersBox },
  props: {
    competition: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      selectedRound: null,
      selectedStage: null,
      selectedPodium: null,
      navigationRestored: false,
      lastKnownCurrentPhaseKey: null,
      publishGate: null,
      publishGateBaseRemainingMs: null,
      publishGateClientStartedAt: null,
      publishGateTickNow: Date.now(),
      publishGateTickerId: null,
      publishGateChannel: null,
      publishGateChannelName: '',
      reviewCounterByRoundId: {},
      nowTs: Date.now(),
      stageTimerId: null,
    };
  },
  created() {
    if (this.appNavigation.competition?.cameFromViewRound) {
      this.showRounds = true;
      this.selectedStage = this.appNavigation.competition?.selectedStage;
      this.selectedRound = this.appNavigation.competition?.selectedRound;
      this.navigationRestored = true;
    }
    this.syncPublishGateFromCompetition(this.competition);
    this.stageTimerId = setInterval(() => {
      this.nowTs = Date.now();
    }, 30000);
  },
  watch: {
    competition: {
      handler(newCompetition) {
        const newCompetitionId = Number(newCompetition?.id ?? 0);
        const currentCompetitionId = Number(this.publishGateChannelName?.split('competition.')[1] ?? 0);
        if (newCompetitionId && currentCompetitionId && newCompetitionId !== currentCompetitionId) {
          this.reviewCounterByRoundId = {};
        }
        this.syncPublishGateFromCompetition(newCompetition);
        this.subscribeToPublishGateUpdates();
      },
      immediate: true,
      deep: false,
    },
    roundsSelectionSyncKey: {
      handler() {
        // Restaurar navegación al volver de ScoreProgram antes de aplicar selección por defecto
        if (!this.navigationRestored && this.appNavigation.competition?.cameFromViewRound) {
          this.showRounds = true;
          this.selectedStage = this.appNavigation.competition?.selectedStage;
          this.selectedRound = this.appNavigation.competition?.selectedRound;
          this.navigationRestored = true;
        }

        const currentPhaseKey = this.getCurrentPhaseViewKey();
        const phaseChanged = Boolean(
          currentPhaseKey
          && this.lastKnownCurrentPhaseKey
          && currentPhaseKey !== this.lastKnownCurrentPhaseKey
        );

        if (!this.navigationRestored && phaseChanged) {
          this.selectDefaultPhase(true);
        } else if (!this.navigationRestored && !this.hasPersistedSelection()) {
          this.selectDefaultPhase(true);
        }

        this.lastKnownCurrentPhaseKey = currentPhaseKey;

      },
      immediate: true,
    },
    selectedRound(val) {
      if (val) this.scrollToSection();
    },
    selectedStage(val) {
      if (val) this.scrollToSection();
    },
    selectedPodium(val) {
      if (val) this.scrollToSection();
    },
    visibleStages: {
      handler(val) {
        if (val && val.length && !this.hasPersistedSelection()) {
          this.selectDefaultPhase(false);
        }
      },
      immediate: true,
    },
  },
  computed: {
    ...mapState('user', ['user']),
    ...mapState("competition",["appNavigation"]),
    ...mapState('round', ['competitionRounds','competitionPodiums']),
    isDualQualification() {
      const raw = this.competition?.is_qualification_dual;
      if (raw === true) return true;
      const numeric = Number(raw);
      return Number.isFinite(numeric) ? numeric === 1 : false;
    },
    isOnlineCompetition() {
      return Number(this.competition?.type_id) === 1;
    },
    competitionTimezone() {
      return resolveCompetitionTimezone(this.competition);
    },
    currentPhaseCalendar() {
      const phase = this.getCurrentPhase();
      if (!phase) return null;

      if (phase.type === 'qualification') {
        const rounds = this.competitionRounds?.filter(
          r => r.round_size_id === null && r.round_number === phase.number
        ) || [];
        const direct = this.findCalendarForRoundNumber(phase.number);
        return this.mergeCalendarWithFallback(direct, rounds);
      }

      if (['vs', 'sc'].includes(phase.type) && phase.stage?.id) {
        const rounds = this.competitionRounds?.filter(
          r => Number(r.round_size_id) === Number(phase.stage.id)
        ) || [];
        const direct = this.findCalendarForStage(phase.stage.id);
        return this.mergeCalendarWithFallback(direct, rounds);
      }

      return null;
    },
    currentPhasePublishAt() {
      const calendar = this.currentPhaseCalendar;
      return this.calendarTimes(calendar).publish;
    },
    currentPhasePublished() {
      const phase = this.getCurrentPhase();
      if (!phase || !this.competitionRounds?.length) return false;

      let rounds = [];
      if (phase.type === 'qualification') {
        rounds = this.competitionRounds.filter(r => r.round_size_id === null && Number(r.round_number) === Number(phase.number));
      } else if (['vs', 'sc'].includes(phase.type)) {
        rounds = this.competitionRounds.filter(r => r.round_size_id === phase.stage.id);
      }

      return rounds.length > 0 && rounds.every(r => this.isPublishedRound(r));
    },
    publishCountdownMs() {
      const publishAt = this.currentPhasePublishAt;
      if (!publishAt) return null;
      const ts = this.parseCompetitionTimestamp(publishAt);
      if (Number.isNaN(ts)) return null;
      return Math.max(ts - this.nowTs, 0);
    },
    isPublishTimeReached() {
      const ms = this.publishCountdownMs;
      // Si no hay publish_at, consideramos habilitado para no bloquear
      if (ms === null) return true;
      return ms <= 0;
    },
    stageCalendars() {
      const calendars = this.competition?.stage_calendars;
      return Array.isArray(calendars) ? calendars : [];
    },
    selectedRoundCalendar() {
      if (this.selectedRound === null || this.selectedRound === 'best') return null;
      // Fallback: construir calendario básico a partir de los rounds si no hay calendar asociado
      const rounds = this.competitionRounds?.filter(r => r.round_size_id === null && r.round_number === this.selectedRound) || [];
      const direct = this.findCalendarForRoundNumber(this.selectedRound);
      return this.mergeCalendarWithFallback(direct, rounds);
    },
    selectedStageCalendar() {
      if (!this.selectedStage) return null;
      const rounds = this.competitionRounds?.filter(r => Number(r.round_size_id) === Number(this.selectedStage)) || [];
      const direct = this.findCalendarForStage(this.selectedStage);
      return this.mergeCalendarWithFallback(direct, rounds);
    },
    selectedRoundCode() {
      return this.pickVerificationCode(this.tableRounds);
    },
    selectedStageCode() {
      const athletes = this.selectedStage ? this.getStageAthletes(this.selectedStage) : [];
      return this.pickVerificationCode(athletes);
    },
    /** 📌 Determina cuántas rondas de calificación hay */
    qualificationRounds() {
      if (!this.isDualQualification) return [1];

      const fromData = [...new Set(
        (this.competitionRounds || [])
          .filter(r => r.round_size_id === null)
          .map(r => Number(r.round_number))
          .filter(n => Number.isFinite(n) && n > 0)
      )].sort((a, b) => a - b);

      if (fromData.length) return fromData;
      return [1, 2];
    },
    bestQualificationActive() {
      if (this.qualificationRounds.includes(2)) {
        const rounds = this.competitionRounds.filter(r => r.round_size_id === null && Number(r.round_number) === 2);

        const allPublished = rounds.length > 0 && rounds.every(r => this.isPublishedRound(r));

        if (allPublished) {
          return rounds;
        }
      }
      return null;
    },
    /** 📌 Obtiene las etapas (stages) de la competición */
    stages() {
      return this.activeRoundSizes;
    },
    activeRoundSizes() {
      const sizes = Array.isArray(this.competition.round_sizes) ? [...this.competition.round_sizes] : [];
      return sizes.filter(this.isStageActiveFlag).sort((a, b) => b.id - a.id);
    },
    tableRounds() {
      if (!this.competitionRounds) return [];

      if (this.selectedRound === 'best') {
        return this.getBestResults();
      }
      return this.getQualificationAthletes(this.selectedRound);
    },
    qualificationAdvancingCount() {
      if (this.selectedRound === null) {
        return 0;
      }

      const nextVisibleStage = (this.visibleStages || [])[0] || null;
      const visibleCount = Number(nextVisibleStage?.athlete_count);
      if (Number.isFinite(visibleCount) && visibleCount > 0) {
        return visibleCount;
      }

      const fallbackStage = (this.stages || [])[0] || null;
      const fallbackCount = Number(fallbackStage?.athlete_count);
      return Number.isFinite(fallbackCount) && fallbackCount > 0 ? fallbackCount : 0;
    },
    stageAdvancingCount() {
      if (this.selectedStage === null || this.competition?.is_vs) {
        return 0;
      }

      const stages = this.visibleStages || [];
      const currentIndex = stages.findIndex(stage => Number(stage?.id) === Number(this.selectedStage));
      if (currentIndex === -1) {
        return 0;
      }

      const nextStage = stages[currentIndex + 1] || null;
      const nextCount = Number(nextStage?.athlete_count);
      return Number.isFinite(nextCount) && nextCount > 0 ? nextCount : 0;
    },
    roundsSelectionSyncKey() {
      if (!Array.isArray(this.competitionRounds) || !this.competitionRounds.length) {
        return 'empty';
      }

      return this.competitionRounds
        .map((round) => {
          const roundId = Number(round?.id ?? 0);
          const roundNumber = Number(round?.round_number ?? 0);
          const stageId = round?.round_size_id === null || round?.round_size_id === undefined
            ? 'qualification'
            : `stage-${Number(round.round_size_id)}`;

          return `${roundId}:${stageId}:${roundNumber}`;
        })
        .sort()
        .join('|');
    },
    canAdvanceStage() {
      return this.areCurrentPhaseRoundsReadyToAdvance();
    },
    publishBlockReasons() {
      const reasons = [];
      const gate = this.activePublishGate;
      const isGatePublished = gate?.state === 'published';

      if (this.currentPhasePublished || isGatePublished) {
        return reasons;
      }

      if (this.currentPhaseHasPendingFeedbackTwos) {
        reasons.push(this.$t('rounds.publish_block_feedback_pending'));
      }

      if (this.currentPhaseHasRoundsBeingEdited) {
        reasons.push(this.$t('rounds.publish_block_judge_editing'));
      }

      if (this.currentPhaseHasRoundsUnderAthleteReview) {
        reasons.push(this.$t('rounds.publish_block_athlete_review'));
      }

      if (this.isOnlineCompetition) {
        return reasons;
      }

      if (!this.showType2Countdown && (!gate || isGatePublished)) {
        reasons.push(this.$t('rounds.publish_block_not_enabled'));
      }

      return reasons;
    },
    currentPhaseHasPendingFeedbackTwos() {
      return this.currentPhaseRounds().some(round => this.getRoundPendingFeedbackTwos(round) > 0);
    },
    currentPhaseHasRoundsBeingEdited() {
      return this.currentPhaseRounds().some(round => Number(round?.status_id) === 22);
    },
    currentPhaseHasRoundsUnderAthleteReview() {
      return this.currentPhaseRounds().some(round => Number(round?.status_id) === 19);
    },
    activePublishGate() {
      if (!this.publishGate) return null;
      if (!this.doesPublishGateMatchCurrentPhase(this.publishGate)) return null;
      return this.publishGate;
    },
    type2PublishCountdownMs() {
      const gate = this.activePublishGate;
      if (!gate || gate.state !== 'countdown') return null;

      const remainingByClientClock = Number.isFinite(gate.availableAtTs)
        ? Math.max(gate.availableAtTs - this.publishGateTickNow, 0)
        : null;

      let remainingByServerClock = null;
      if (
        Number.isFinite(gate.availableAtTs)
        && Number.isFinite(gate.serverNowTs)
        && this.publishGateClientStartedAt
      ) {
        const elapsedMs = Math.max(this.publishGateTickNow - this.publishGateClientStartedAt, 0);
        remainingByServerClock = Math.max(gate.availableAtTs - (gate.serverNowTs + elapsedMs), 0);
      }

      if (Number.isFinite(remainingByClientClock) && Number.isFinite(remainingByServerClock)) {
        return Math.min(remainingByClientClock, remainingByServerClock);
      }

      if (Number.isFinite(remainingByClientClock)) {
        return remainingByClientClock;
      }

      if (Number.isFinite(remainingByServerClock)) {
        return remainingByServerClock;
      }

      if (!Number.isFinite(this.publishGateBaseRemainingMs) || !this.publishGateClientStartedAt) {
        return null;
      }

      const elapsedMs = Math.max(this.publishGateTickNow - this.publishGateClientStartedAt, 0);
      return Math.max(this.publishGateBaseRemainingMs - elapsedMs, 0);
    },
    isType2PublishTimeReached() {
      const gate = this.activePublishGate;
      if (!gate) return false;
      if (gate.state === 'ready') return true;
      if (gate.state !== 'countdown') return false;

      const remainingMs = this.type2PublishCountdownMs;
      return remainingMs !== null && remainingMs <= 0;
    },
    isType2PublishGateOpen() {
      if (this.isOnlineCompetition) return false;
      const gate = this.activePublishGate;
      if (!gate) return false;
      return Boolean(gate.canPublish) || this.isType2PublishTimeReached;
    },
    isType2PhaseReadyToPublish() {
      if (this.isOnlineCompetition) return false;
      return this.areCurrentPhaseRoundsReadyToPublish() || this.areCurrentPhaseRoundsValidated();
    },
    canPublishType2() {
      if (this.isOnlineCompetition) return false;
      const gate = this.activePublishGate;
      if (!gate) return false;
      if (this.currentPhasePublished || gate.state === 'published') return false;
      if (this.currentPhaseHasPendingFeedbackTwos) return false;
      if (this.currentPhaseHasRoundsBeingEdited) return false;
      if (this.currentPhaseHasRoundsUnderAthleteReview) return false;
      if (!this.isType2PublishGateOpen) return false;
      return this.isType2PhaseReadyToPublish;
    },
    isType2PhaseStatusReady() {
      if (this.isOnlineCompetition) return false;
      return Boolean(this.activePublishGate);
    },
    showType2Countdown() {
      const gate = this.activePublishGate;
      if (!gate) return false;
      return gate.state === 'countdown' && !this.isType2PublishTimeReached;
    },
    phaseName() {
      const phase = this.getCurrentPhase();
      if (!phase) return '';

      switch (phase.type) {
        case 'qualification':
          return this.$t('rounds.qualifying') + (phase.number === 2 ? ' 2' : ' 1');
        case 'vs':
        case 'sc':
          return phase.stage?.id ? this.$t(`rounds.stage${phase.stage.id}`) : '';
        case 'end':
          return this.$t('rounds.podium');
        default:
          return '';
      }
    },
    nextPhaseName() {
      const next = this.getNextPhase();
      if (!next) return '';

      switch (next.type) {
        case 'qualification':
          return this.$t('rounds.qualifying') + (next.number === 2 ? ' 2' : ' 1');
        case 'vs':
          return next.stage?.id ? this.getStageName(next.stage.id) : '';
        case 'sc':
          return next.stage?.id ? this.getStageName(next.stage.id) : '';
        case 'end':
          return this.$t('rounds.podium');
        default:
          return '';
      }
    },
    visiblePhaseKeys() {
      return this.getVisiblePhases().map(phase => {
        if (phase.type === 'qualification') {
          return `qualification-${phase.number}`;
        } else if (phase.type === 'vs') {
          return `vs-${phase.stage.id}`;
        } else if (phase.type === 'sc') {
          return `sc-${phase.stage.id}`;
        }
        return null;
      });
    },
    visibleQualificationRounds() {
      return this.qualificationRounds.filter(round =>
          this.visiblePhaseKeys.includes(`qualification-${round}`)
      );
    },
    visibleStages() {
      return this.stages.filter(stage =>
          this.visiblePhaseKeys.includes(`vs-${stage.id}`) || this.visiblePhaseKeys.includes(`sc-${stage.id}`)
      );
    },
    qualificationTabs() {
      const tabs = (this.visibleQualificationRounds || []).map((roundNumber) => ({
        key: `qualification-${roundNumber}`,
        value: roundNumber,
        label: `${this.$t("rounds.qualifying")} ${roundNumber}`,
        status: this.getQualificationPhaseStatus(roundNumber),
        variant: 'qualification'
      }));

      if (this.bestQualificationActive) {
        tabs.push({
          key: 'qualification-best',
          value: 'best',
          label: this.$t("rounds.best_result"),
          status: this.getBestQualificationStatus(),
          variant: 'best'
        });
      }

      return tabs;
    },
    stageTabs() {
      return (this.visibleStages || []).map((stage) => ({
        key: `stage-${stage.id}`,
        value: stage.id,
        label: this.getStageName(stage.id),
        status: this.getStagePhaseStatus(stage.id),
        count: Number(stage?.athlete_count || 0)
      }));
    }
  },
  mounted() {
    this.getVisiblePhases();
    // Si no viene navegación previa, abre la fase en curso o la última disponible
    if (!this.navigationRestored) {
      this.selectDefaultPhase(true);
    }
    this.subscribeToPublishGateUpdates();
  },
  beforeUnmount() {
    if (this.stageTimerId) {
      clearInterval(this.stageTimerId);
    }
    this.stopPublishGateTicker();
    this.unsubscribeFromPublishGateUpdates();
  },
  methods: {
    ...mapActions("competition",["updateAppNavigationZone","clearAppNavigationZone"]),
    isOwnerOrganizer,
    isInnerAthlete,
    parseCompetitionTimestamp(raw) {
      const parsed = parseCompetitionDate(raw, this.competitionTimezone);
      return parsed instanceof Date ? parsed.getTime() : NaN;
    },
    subscribeToPublishGateUpdates() {
      const competitionId = Number(this.competition?.id);
      if (!Number.isFinite(competitionId) || competitionId <= 0) {
        this.unsubscribeFromPublishGateUpdates();
        return;
      }

      const channelName = `competition.${competitionId}`;
      if (this.publishGateChannelName === channelName && this.publishGateChannel) {
        return;
      }

      this.unsubscribeFromPublishGateUpdates();

      try {
        const channel = echoService.channel(channelName);
        channel.listen('.CompetitionPublishGateUpdated', this.handlePublishGateUpdated);
        channel.listen('.ReviewCounter', this.handleReviewCounterUpdated);
        this.publishGateChannel = channel;
        this.publishGateChannelName = channelName;
      } catch (error) {
        console.warn('[PublishGate] failed to subscribe', error?.message || error);
      }
    },
    unsubscribeFromPublishGateUpdates() {
      if (this.publishGateChannel) {
        try {
          this.publishGateChannel.stopListening('.CompetitionPublishGateUpdated');
          this.publishGateChannel.stopListening('.ReviewCounter');
        } catch (error) {
          /* ignore */
        }
      }

      this.publishGateChannel = null;
      this.publishGateChannelName = '';
    },
    handlePublishGateUpdated(event = {}) {
      const competitionId = Number(
        event?.competition_id
        ?? event?.competition?.id
        ?? event?.publish_gate?.competition_id
        ?? this.competition?.id
      );

      if (competitionId && Number(competitionId) !== Number(this.competition?.id)) {
        return;
      }

      this.applyPublishGateState(event);
    },
    normalizeReviewCounterPayload(event = {}) {
      const payload = event?.data && typeof event.data === 'object'
        ? event.data
        : event;

      const roundId = Number(payload?.round_id ?? payload?.roundId ?? 0);
      const count = Number(
        payload?.count_negative_feedback
        ?? payload?.negative_feedback_count
        ?? payload?.count
        ?? 0
      );

      if (!Number.isFinite(roundId) || roundId <= 0) {
        return null;
      }

      return {
        roundId,
        count: Number.isFinite(count) ? count : 0,
      };
    },
    handleReviewCounterUpdated(event = {}) {
      const normalized = this.normalizeReviewCounterPayload(event);
      if (!normalized) return;

      this.reviewCounterByRoundId = {
        ...this.reviewCounterByRoundId,
        [normalized.roundId]: normalized.count,
      };
    },
    syncPublishGateFromCompetition(competition) {
      const payload = competition?.publish_gate || competition?.publishGate || null;
      this.applyPublishGateState(payload, { clearWhenMissing: true });
    },
    normalizePublishGatePayload(raw = null) {
      const source = raw?.publish_gate || raw?.publishGate || raw?.gate || raw;
      if (!source || typeof source !== 'object') return null;

      const rawState = source.state || source.status || source.gate_state || source.gateStatus;
      const state = String(rawState || '').trim().toLowerCase();
      if (!state) return null;

      const rawPhaseKey = source.phase_key || source.phaseKey || source.phase || null;
      const phaseKey = rawPhaseKey === null || rawPhaseKey === undefined
        ? null
        : String(rawPhaseKey).trim().toLowerCase();
      const startedAt = source.started_at || source.startedAt || null;
      const availableAt = source.available_at || source.availableAt || null;
      const serverNow = source.server_now || source.serverNow || null;
      const startedAtTs = startedAt ? new Date(startedAt).getTime() : NaN;
      const availableAtTs = availableAt ? new Date(availableAt).getTime() : NaN;
      const serverNowTs = serverNow ? new Date(serverNow).getTime() : NaN;
      const canPublishRaw = source.can_publish ?? source.canPublish;
      const normalizedCanPublish = typeof canPublishRaw === 'string'
        ? canPublishRaw.trim().toLowerCase()
        : canPublishRaw;
      const canPublish = canPublishRaw === undefined || canPublishRaw === null
        ? state === 'ready'
        : ['1', 'true', 'yes', 'ready'].includes(String(normalizedCanPublish));

      let initialRemainingMs = null;
      if (state === 'countdown') {
        const remainingByClientClock = Number.isFinite(availableAtTs)
          ? Math.max(availableAtTs - Date.now(), 0)
          : null;
        const remainingByServerClock = Number.isFinite(availableAtTs) && Number.isFinite(serverNowTs)
          ? Math.max(availableAtTs - serverNowTs, 0)
          : null;

        if (Number.isFinite(remainingByClientClock) && Number.isFinite(remainingByServerClock)) {
          initialRemainingMs = Math.min(remainingByClientClock, remainingByServerClock);
        } else if (Number.isFinite(remainingByClientClock)) {
          initialRemainingMs = remainingByClientClock;
        } else if (Number.isFinite(remainingByServerClock)) {
          initialRemainingMs = remainingByServerClock;
        }
      }

      return {
        state,
        phaseKey,
        startedAt,
        availableAt,
        serverNow,
        startedAtTs,
        availableAtTs,
        serverNowTs,
        canPublish,
        initialRemainingMs,
      };
    },
    applyPublishGateState(raw, { clearWhenMissing = false } = {}) {
      const normalized = this.normalizePublishGatePayload(raw);
      if (!normalized) {
        if (clearWhenMissing) {
          this.publishGate = null;
          this.publishGateBaseRemainingMs = null;
          this.publishGateClientStartedAt = null;
          this.publishGateTickNow = Date.now();
          this.stopPublishGateTicker();
        }
        return;
      }

      this.publishGate = normalized;
      this.publishGateBaseRemainingMs = Number.isFinite(normalized.initialRemainingMs)
        ? normalized.initialRemainingMs
        : null;
      this.publishGateClientStartedAt = Date.now();
      this.publishGateTickNow = Date.now();

      if (normalized.state === 'countdown') {
        this.startPublishGateTicker();
      } else {
        this.stopPublishGateTicker();
      }
    },
    startPublishGateTicker() {
      if (this.publishGateTickerId) return;
      this.publishGateTickNow = Date.now();
      this.publishGateTickerId = setInterval(() => {
        this.publishGateTickNow = Date.now();
      }, 1000);
    },
    stopPublishGateTicker() {
      if (this.publishGateTickerId) {
        clearInterval(this.publishGateTickerId);
        this.publishGateTickerId = null;
      }
    },
    getCurrentPhaseGateKeys() {
      const phase = this.getCurrentPhase();
      if (!phase) return [];

      if (phase.type === 'qualification') {
        return this.uniqueNormalizedKeys([
          String(phase.number),
          `q_${phase.number}`,
          `qualification-${phase.number}`,
          `qualification_${phase.number}`,
          `q${phase.number}`,
        ]);
      }

      if ((phase.type === 'vs' || phase.type === 'sc') && phase.stage?.id) {
        return this.uniqueNormalizedKeys(this.getStageGateKeys(phase.stage, phase.type));
      }

      if (phase.type === 'end') {
        return this.uniqueNormalizedKeys(['end', 'podium']);
      }

      return [];
    },
    uniqueNormalizedKeys(values = []) {
      return [...new Set(
        (Array.isArray(values) ? values : [values])
          .filter(value => value !== null && value !== undefined && value !== '')
          .map(value => String(value).trim().toLowerCase())
          .filter(Boolean)
      )];
    },
    inferStageSemanticGateKeys(stage) {
      const stageId = Number(stage?.id);
      const semanticById = {
        1: ['final'],
        2: ['round_4', 'semifinal', 'semi_final'],
        3: ['round_8', 'quarterfinal', 'quarter_final'],
        4: ['round_16', 'eighthfinal', 'eighth_final'],
        5: ['round_32', 'sixteenthfinal', 'sixteenth_final'],
      };

      return semanticById[stageId] || [];
    },
    getStageGateKeys(stage, phaseType = null) {
      if (!stage?.id) return [];

      const stageId = Number(stage.id);
      const calendar = this.findCalendarForStage(stageId);
      const stageCode = this.stageCodeForStage(stageId);

      return [
        String(stageId),
        `s_${stageId}`,
        `stage-${stageId}`,
        `stage${stageId}`,
        phaseType ? `${phaseType}-${stageId}` : null,
        stage?.code,
        stage?.stage_code,
        stage?.key,
        stage?.slug,
        stageCode,
        calendar?.stage_code,
        ...this.inferStageSemanticGateKeys(stage),
      ];
    },
    getCurrentPhaseViewKey() {
      const phase = this.getCurrentPhase();
      if (!phase) return null;

      if (phase.type === 'qualification') {
        return `qualification-${phase.number}`;
      }

      if ((phase.type === 'vs' || phase.type === 'sc') && phase.stage?.id) {
        return `${phase.type}-${phase.stage.id}`;
      }

      if (phase.type === 'end') {
        return 'podium';
      }

      return null;
    },
    doesPublishGateMatchCurrentPhase(gate) {
      if (!gate) return false;
      if (!gate.phaseKey) return true;
      return this.getCurrentPhaseGateKeys().includes(String(gate.phaseKey).trim().toLowerCase());
    },
    getOrderedCompetitionPhases() {
      const isVs = this.competition?.is_vs;
      const phases = [{ type: 'qualification', number: 1 }];

      if (this.isDualQualification) {
        phases.push({ type: 'qualification', number: 2 });
      }

      this.activeRoundSizes.forEach((stage) => {
        phases.push({ type: isVs ? 'vs' : 'sc', stage });
      });

      return phases;
    },
    getCurrentPhase() {
      if (!this.competition || !this.competitionRounds?.length) return null;

      const phases = this.getOrderedCompetitionPhases();
      for (let idx = phases.length - 1; idx >= 0; idx -= 1) {
        const phase = phases[idx];
        const rounds = this.currentPhaseRounds(phase);
        if (rounds.length > 0) {
          return phase;
        }
      }

      // Sin rondas aún: primera fase teórica
      return phases[0] || null;
    },
    getNextPhase() {
      if (!this.competition || !this.competitionRounds?.length) return null;
      const current = this.getCurrentPhase();
      if (!current) return { type: 'end' };

      const orderedPhases = this.getOrderedCompetitionPhases();

      const currentIndex = orderedPhases.findIndex((phase) => {
        if (phase.type !== current.type) return false;
        if (phase.type === 'qualification') return Number(phase.number) === Number(current.number);
        return Number(phase.stage?.id) === Number(current.stage?.id);
      });

      if (currentIndex < 0) return { type: 'end' };
      const next = orderedPhases[currentIndex + 1];
      if (!next) return { type: 'end' };

      return next;
    },
    currentPhaseRounds(phaseArg = null) {
      const phase = phaseArg || this.getCurrentPhase();
      if (!phase || !this.competitionRounds?.length) return [];

      if (phase.type === 'qualification') {
        return this.competitionRounds.filter(
          r => r.round_size_id === null && Number(r.round_number) === Number(phase.number)
        );
      }

      if ((phase.type === 'vs' || phase.type === 'sc') && phase.stage?.id) {
        return this.competitionRounds.filter(r => Number(r.round_size_id) === Number(phase.stage.id));
      }

      return [];
    },
    // Devuelve true solo si la fase ya tiene rondas activas
    isPhaseActive(phase) {
      if (!phase) return false;
      if (phase.type === 'qualification') {
        return this.qualificationRounds.includes(phase.number);
      }
      if (phase.type === 'vs' || phase.type === 'sc') {
        return this.isStageActiveFlag(phase.stage);
      }
      return false;
    },
    // Marca un stage como activo si el backend lo indica; si no trae flag, se asume activo
    isStageActiveFlag(stage) {
      if (!stage) return false;
      if (stage.active === false) return false;
      if (stage.active === true) return true;
      if (stage.activated_at) return true;
      return true;
    },
    isReadyToPublishRoundStatus(statusId) {
      return [7, 11, 12, 23].includes(Number(statusId));
    },
    isPublishedRoundStatus(statusId) {
      return [10, 11, 12, 23].includes(Number(statusId));
    },
    isPublishedRound(round) {
      return this.isPublishedRoundStatus(round?.status_id);
    },
    areCurrentPhaseRoundsReadyToPublish(asBoolean = true) {
      const rounds = this.currentPhaseRounds();

      if (asBoolean) {
        return rounds.length > 0 && rounds.every(r => this.isReadyToPublishRoundStatus(r.status_id));
      } else {
        return rounds.map(r => r.id); // 🟢 Solo IDs
      }
    },
    areCurrentPhaseRoundsReadyToAdvance(asBoolean = true) {
      const rounds = this.currentPhaseRounds();

      const allReady = rounds.length > 0 && rounds.every(r => this.isPublishedRound(r));

      if (asBoolean) {
        return allReady;
      } else {
        return allReady ? rounds[0]?.round_number ?? null : null;
      }
    },
    areCurrentPhaseRoundsValidated() {
      const rounds = this.currentPhaseRounds();
      if (!rounds.length) return false;
      return rounds.every(round => round.validated_at !== null);
    },
    getVisiblePhases() {
      if (!this.competition || !this.competitionRounds?.length) return [];

      const visiblePhases = [];
      const isVs = this.competition.is_vs;

      const roundSizes = this.activeRoundSizes;
      const rounds = [...this.competitionRounds].sort((a, b) => a.round_number - b.round_number);

      // Fases de calificación presentes (según datos reales)
      this.qualificationRounds.forEach((number) => {
        const qualificationRounds = rounds.filter(
          r => r.round_size_id === null && Number(r.round_number) === Number(number)
        );
        if (qualificationRounds.length > 0) {
          visiblePhases.push({
            type: 'qualification',
            number,
            status: this._getPhaseStatus(qualificationRounds)
          });
        }
      });

      // Fases VS (cuartos, semis, final...)
      const isType =isVs ? 'vs' : 'sc';
      roundSizes.forEach(stage => {
        const vsRounds = rounds.filter(r => r.round_size_id === stage.id);
        if (vsRounds.length > 0) {
          visiblePhases.push({
            type: isType,
            stage,
            status: this._getPhaseStatus(vsRounds)
          });
        }
      });

      // Solo etapas completadas o la actual en curso
      const current = visiblePhases.find(p => p.status === 'in_progress');
      const finished = visiblePhases.filter(p => p.status === 'finished');
      const result = current ? [...finished, current] : finished;
     
      return result;
    },
    areAllRoundsValidated() {
      if (!this.competitionRounds || !this.competitionRounds.length) return false;

      return this.competitionRounds.every(round => round.validated_at !== null);
    },
// 🔧 Función auxiliar para determinar el estado de la etapa
    _getPhaseStatus(rounds) {
      const allFinished = rounds.every(r => this.isPublishedRound(r));
      const anyInProgress = rounds.some(r => [6, 7, 13, 16, 17, 20, 21].includes(r.status_id));

      if (allFinished) return 'finished';
      if (anyInProgress) return 'in_progress';
      return 'waiting';
    },
    getQualificationPhaseStatus(roundNumber) {
      const rounds = (this.competitionRounds || []).filter(
        r => r.round_size_id === null && Number(r.round_number) === Number(roundNumber)
      );

      return rounds.length ? this._getPhaseStatus(rounds) : 'waiting';
    },
    getBestQualificationStatus() {
      const rounds = Array.isArray(this.bestQualificationActive) ? this.bestQualificationActive : [];
      return rounds.length ? this._getPhaseStatus(rounds) : 'waiting';
    },
    getStagePhaseStatus(stageId) {
      const rounds = (this.competitionRounds || []).filter(
        r => Number(r.round_size_id) === Number(stageId)
      );

      return rounds.length ? this._getPhaseStatus(rounds) : 'waiting';
    },
    phaseStatusLabel(status) {
      const labels = {
        finished: this.$t('judge.published'),
        in_progress: this.$t('judge.review'),
        waiting: this.$t('judge.pending')
      };

      return labels[status] || status;
    },
    /** 📌 Selecciona una ronda de calificación */
    selectRound(round) {
      this.selectedRound = round;
      this.selectedStage = null;
      this.selectedPodium = null;
    },

    /** 📌 Selecciona una etapa */
    selectStage(stageId) {
      this.selectedStage = stageId;
      this.selectedRound = null;
      this.selectedPodium = null;
    },

    selectRoundAuto(roundNumber) {
      this.selectedRound = roundNumber;
      this.selectedStage = null;
      this.selectedPodium = null;
    },

    hasPersistedSelection() {
      if (this.selectedPodium !== null) {
        return Array.isArray(this.competitionPodiums) && this.competitionPodiums.length > 0;
      }

      if (this.selectedStage !== null) {
        return (this.visibleStages || []).some(stage => Number(stage.id) === Number(this.selectedStage));
      }

      if (this.selectedRound !== null) {
        if (this.selectedRound === 'best') {
          return Boolean(this.bestQualificationActive);
        }

        return (this.visibleQualificationRounds || []).some(round => Number(round) === Number(this.selectedRound));
      }

      return false;
    },

    selectDefaultPhase(force = false) {
      if (this.navigationRestored) return; // already set from navigation meta
      if (!force && this.hasPersistedSelection()) return;

      const phase = this.getCurrentPhase();
      const stages = this.visibleStages || [];
      const hasPodium = Array.isArray(this.competitionPodiums) && this.competitionPodiums.length > 0;
      const statusId = Number(this.competition?.status_id ?? this.competition?.statusId);

      // Si la competición está finalizada (8), prioriza podio
      if (statusId === 8 && hasPodium) {
        this.selectPodium();
        return;
      }

      // Podium cuando la fase actual es fin
      if (phase && phase.type === 'end') {
        if (hasPodium) {
          this.selectPodium();
          return;
        }
      }

      // Prefer current stage in progress when present
      if (phase && (phase.type === 'vs' || phase.type === 'sc') && phase.stage?.id) {
        const currentId = Number(phase.stage.id);
        const already = Number(this.selectedStage) === currentId;
        const exists = stages.some(s => Number(s.id) === currentId);
        if ((force || this.selectedStage === null || !already) && exists) {
          this.selectStage(currentId);
          return;
        }
      }

      // Qualification current phase
      if (phase && phase.type === 'qualification') {
        const roundNum = phase.number;
        const already = Number(this.selectedRound) === Number(roundNum);
        if (force || this.selectedRound === null || !already) {
          this.selectRoundAuto(roundNum);
          return;
        }
      }

      // Fallback: pick an in-progress stage or the first available stage
      if (this.selectedStage === null && stages.length) {
        const inProgress = stages.find(s => this.stageStatus(s.id) === 'in_progress');
        if (inProgress) {
          this.selectStage(inProgress.id);
          return;
        }
        this.selectStage(stages[0].id);
        return;
      }

      // Fallback qualification: pick the highest visible round
      if (this.selectedRound === null && this.visibleQualificationRounds?.length) {
        const roundsDesc = [...this.visibleQualificationRounds].sort((a, b) => b - a);
        this.selectRoundAuto(roundsDesc[0]);
        return;
      }

      // Fallback podium if nothing else
      if (this.selectedPodium === null && hasPodium) {
        this.selectPodium();
      }
    },

    stageStatus(stageId) {
      const rounds = (this.competitionRounds || []).filter(r => Number(r.round_size_id) === Number(stageId));
      return this._getPhaseStatus(rounds);
    },

    selectPodium() {
      this.selectedPodium = true;
      this.selectedStage = null;
      this.selectedRound = null;
    },

    /** 📌 Obtiene y ordena los atletas por puntuación en la ronda seleccionada */
    getQualificationAthletes(roundNumber) {
      let filteredRounds = this.competitionRounds?.filter(
          round => round.round_size_id === null && round.round_number === roundNumber
      );
      if (!filteredRounds) return [];
      const calendar = this.findCalendarForRoundNumber(roundNumber);
      const fallbackPublishAt = this.calendarTimes(calendar).publish;

      const publishedMap = new Map();
      const noSubmissionMap = new Map();
      const otherMap = new Map();

      const upsert = (map, payload) => {
        const existing = map.get(payload.id);
        if (!existing || payload.total_score > existing.total_score) {
          map.set(payload.id, payload);
        }
      };

      const sortByName = (a, b) => {
        const surnameCmp = (a.surname || '').localeCompare(b.surname || '');
        if (surnameCmp !== 0) return surnameCmp;
        return (a.name || '').localeCompare(b.name || '');
      };

      filteredRounds.forEach(round => {
        const payload = {
          id: round.user.id,
          round_id: round.id,
          round_number: round.round_number,
          round_size_id: round.round_size_id,
          name: round.user.name,
          surname: round.user.surname,
          img: round.user.img_path,
          total_score: parseFloat(round.total_score) || 0,
          status_id: round.status_id,
          completed_scoring_at: round.completed_scoring_at,
          validated_at: round.validated_at,
          injured_at: round.injured_at,
          disqualified_at: round.disqualified_at,
          verification_code: round.verification_code,
          video_link: round.video_link,
          publish_at: round.publish_at || fallbackPublishAt
        };

        if (this.isPublishedRound(round) && Number(round.status_id) !== 23) {
          upsert(publishedMap, payload);
          return;
        }
        if (round.status_id === 23) {
          upsert(noSubmissionMap, payload);
          return;
        }
        upsert(otherMap, payload);
      });

      const published = [...publishedMap.values()].sort((a, b) => b.total_score - a.total_score);
      const other = [...otherMap.values()];
      const noSubmission = [...noSubmissionMap.values()].sort(sortByName);
      // Mantiene todos los atletas: publicados primero, luego otros estados, y al final sin entrega
      return [...published, ...other, ...noSubmission];
    },

    /** 📌 Obtiene los mejores resultados si hay doble calificación */
    getBestResults() {
      if (!this.competition.is_qualification_dual) return [];

      let allResults = [];
      [1, 2].forEach(roundNumber => {
        allResults = allResults.concat(this.getQualificationAthletes(roundNumber));
      });

      let bestResultsMap = new Map();

      // Filtramos atletas descalificados
      const validResults = allResults.filter(athlete => {
        return !athlete.disqualified_at;
      });

      // Agrupamos por atleta y elegimos su mejor resultado
      validResults.forEach(athlete => {
        const existing = bestResultsMap.get(athlete.id);
        if (!existing || athlete.total_score > existing.total_score) {
          bestResultsMap.set(athlete.id, athlete);
        }
      });

      // Convertimos en array ordenado de mayor a menor
      const bestResultsSorted = [...bestResultsMap.values()].sort((a, b) => b.total_score - a.total_score);

      // Buscamos el siguiente roundSize (ordenado por id ascendente)
      const nextRoundSize = this.competition.round_sizes
          ?.slice()
          .sort((a, b) => b.id - a.id)
          .at(0);

      if (!nextRoundSize) return bestResultsSorted;

      // Devolvemos solo los primeros N atletas
      return bestResultsSorted.slice(0, nextRoundSize.athlete_count);
    },


    /** 📌 Genera los enfrentamientos del bracket automáticamente */
/*    getBracketMatches(stageId) {
      const rounds = this.competitionRounds.filter(r => r.round_size_id === stageId);

      const isVS = rounds.some(r => r.vs_group || r.vs_match_number);
      if (!isVS) {
        // lógica actual que usás por score
        const sortedAthletes = this.getStageAthletes(stageId);
        const stage = this.stages.find(s => s.id === stageId);
        let pairs = [];
        let athleteCount = stage.athlete_count;
        let matchOrder = [];

        for (let i = 0; i < athleteCount / 2; i++) {
          matchOrder.push([i, athleteCount - 1 - i]);
        }

        matchOrder.forEach(pair => {
          pairs.push([
            sortedAthletes[pair[0]] || null,
            sortedAthletes[pair[1]] || null
          ]);
        });

        return pairs;
      }

      // 🥊 Etapa VS: formar matches por grupo y número
      const groupedMatches = {};

      const groupPriority = {
        "F": 1,    // Final
        "3P": 2,   // Tercer puesto
        "A": 3,
        "B": 4,
        "C": 5,
        "D": 6
      };

      rounds.forEach(round => {
        const key = `${round.vs_group || "A"}-${round.vs_match_number || 0}`;
        if (!groupedMatches[key]) groupedMatches[key] = [];
        groupedMatches[key].push({
          id: round.user.id,
          round_id: round.id,
          name: round.user.name,
          surname: round.user.surname,
          img: round.user.img_path,
          total_score: parseFloat(round.total_score),
          status_id: round.status_id,
          is_vs_winner: round.is_vs_winner,
          validated_at: round.validated_at
        });
      });

      // Ordenar claves A-1, A-2, B-1, B-2...
      const sortedKeys = Object.keys(groupedMatches).sort((a, b) => {
        const [groupA, matchA] = a.split("-");
        const [groupB, matchB] = b.split("-");

        const priorityA = groupPriority[groupA] ?? 99;
        const priorityB = groupPriority[groupB] ?? 99;

        if (priorityA === priorityB) {
          return parseInt(matchA) - parseInt(matchB);
        }

        return priorityA - priorityB;
      });

      return sortedKeys.map(key => {
        const pair = groupedMatches[key];
        return [pair[0] || null, pair[1] || null];
      });
    },*/
    getBracketMatches(stageId) {
      const rounds = this.competitionRounds.filter(r => r.round_size_id === stageId);
      const normalizedRounds = rounds.map(round => ({
        id: round.user.id,
        round_id: round.id,
        name: round.user.name,
        surname: round.user.surname,
        img: round.user.img_path,
        total_score: parseFloat(round.total_score),
        status_id: round.status_id,
        completed_scoring_at: round.completed_scoring_at,
        is_vs_winner: round.is_vs_winner,
        publish_at: round.publish_at,
        validated_at: round.validated_at,
        injured_at: round.injured_at,
        disqualified_at: round.disqualified_at,
        vs_group: round.vs_group,
        vs_match_number: round.vs_match_number,
        vs_bracket: round.vs_bracket || null
      }));

      return assembleVsBracketMatches(normalizedRounds, {
        stageId,
        placeholderName: this.$t('rounds.placeholder_player')
      });
    },
    getBracketGroupPriority(group, athleteCount = 0) {
      if (group === 'F') return 0;
      if (group === '3P') return 1;

      const order = athleteCount === 32
        ? ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H']
        : athleteCount === 16
          ? ['A', 'B', 'C', 'D']
          : ['A', 'B'];

      const index = order.indexOf(group);
      return index === -1 ? 99 : index + 10;
    },
    /** 📌 Obtiene los atletas de una etapa específica */
   /* getStageAthletes(stageId) {
      if (!this.competition || !this.competitionRounds) return [];

      // Obtener rondas de esa etapa
      let rounds = this.competitionRounds.filter(
          round => round.round_size_id === stageId
      );

      // Si es una etapa VS
      const isVS = rounds.some(round => round.vs_group || round.vs_match_number);

      if (isVS) {
        return rounds
            .sort((a, b) => {
              if (a.vs_group === b.vs_group) {
                return a.vs_match_number - b.vs_match_number;
              }
              return a.vs_group.localeCompare(b.vs_group);
            })
            .map(round => ({
              id: round.user.id,
              round_id: round.id,
              name: round.user.name,
              surname: round.user.surname,
              img: round.user.img_path,
              total_score: parseFloat(round.total_score),
              status_id: round.status_id,
              group: round.vs_group,
              match_number: round.vs_match_number,
              validated_at: round.validated_at
            }));
      }

      // No es VS
      const hasPublished = rounds.every(round => round.status_id === 10);

      const result = rounds.map(round => ({
        id: round.user.id,
        round_id: round.id,
        name: round.user.name,
        surname: round.user.surname,
        img: round.user.img_path,
        total_score: parseFloat(round.total_score),
        status_id: round.status_id,
        validated_at: round.validated_at
      }));

      return hasPublished ? result.sort((a, b) => b.total_score - a.total_score) : result;
    },*/
    getStageAthletes(stageId) {
      if (!this.competition || !this.competitionRounds) return [];

      // Obtener rondas de esa etapa
      const rounds = this.competitionRounds.filter(round => round.round_size_id === stageId);
      const fallbackPublishAt = this.calendarTimes(this.findCalendarForStage(stageId)).publish;

      const published = [];
      const noSubmission = [];
      const other = [];

      const sortByName = (a, b) => {
        const surnameCmp = (a.surname || '').localeCompare(b.surname || '');
        if (surnameCmp !== 0) return surnameCmp;
        return (a.name || '').localeCompare(b.name || '');
      };

      rounds.forEach(round => {
        const payload = {
          id: round.user.id,
          round_id: round.id,
          round_number: round.round_number,
          round_size_id: round.round_size_id,
          name: round.user.name,
          surname: round.user.surname,
          img: round.user.img_path,
          total_score: parseFloat(round.total_score) || 0,
          status_id: round.status_id,
          completed_scoring_at: round.completed_scoring_at,
          validated_at: round.validated_at,
          injured_at: round.injured_at,
          disqualified_at: round.disqualified_at,
          verification_code: round.verification_code,
          video_link: round.video_link,
          publish_at: round.publish_at || fallbackPublishAt
        };

        if (this.isPublishedRound(round) && Number(round.status_id) !== 23) {
          published.push(payload);
          return;
        }
        if (round.status_id === 23) {
          noSubmission.push(payload);
          return;
        }
        other.push(payload);
      });

      const publishedSorted = [...published].sort((a, b) => b.total_score - a.total_score);
      const otherSorted = [...other];
      const noSubmissionSorted = [...noSubmission].sort(sortByName);
      // Mantiene todos los atletas: publicados primero, luego otros estados, y al final sin entrega
      return [...publishedSorted, ...otherSorted, ...noSubmissionSorted];
    },

    /** 📌 Obtiene el nombre de la etapa seleccionada */
    getStageName(stageId) {
      if (!this.stages) return [];
      if (!stageId) return '';

      const stage = this.stages.find(s => s.id === stageId);
      if (!stage) {
        return '';
      }
      const key = `rounds.stage${stage.id}`;
      const transName = this.$te(key) ? this.$t(key) : stage.name;

      return transName || "";
    },
    advanceStage() {
      const readyRound = this.areCurrentPhaseRoundsReadyToAdvance(false);
      this.$emit("advance-stage", this.competition.id,readyRound);
    },
    publishStage() {
      if (this.currentPhasePublished) return;
      if (this.currentPhaseHasPendingFeedbackTwos) return;
      if (this.currentPhaseHasRoundsBeingEdited) return;
      if (this.currentPhaseHasRoundsUnderAthleteReview) return;
      if (this.isOnlineCompetition) {
        // Online: allow publish when publish_at is reached; no status gating
        this.$emit("publish-stage", this.areCurrentPhaseRoundsReadyToPublish(false));
        return;
      }

      if (!this.canPublishType2) return;
      this.$emit("publish-stage", this.areCurrentPhaseRoundsReadyToPublish(false));
    },
    viewRound({athlete,selectedStage,selectedRound}) {
      this.$emit("view-round", {athlete,selectedStage,selectedRound});
    },
    reviewRound({athlete,selectedStage,selectedRound}) {
      this.$emit("review-round", {athlete,selectedStage,selectedRound});
    },
    canPublishStage() {
        return this.areCurrentPhaseRoundsReadyToPublish();
    },
    getRoundPendingFeedbackTwos(round = null) {
      const roundId = Number(round?.id ?? round?.round_id ?? 0);
      if (Number.isFinite(roundId) && roundId > 0 && Object.prototype.hasOwnProperty.call(this.reviewCounterByRoundId, roundId)) {
        return this.reviewCounterByRoundId[roundId];
      }

      const raw = round?.athlete_feedback_twos
        ?? round?.athlete_feedback_two
        ?? round?.feedback_two
        ?? round?.feedback_twos
        ?? round?.negative_feedback_count
        ?? 0;

      const count = Number(raw);
      return Number.isFinite(count) ? count : 0;
    },
    getCountdownFormatted() {
      if (this.isOnlineCompetition) {
        const ms = this.publishCountdownMs;
        return this.formatAdaptiveCountdown(ms);
      }

      return this.formatAdaptiveCountdown(this.type2PublishCountdownMs);
    },
    stageCodeForRound(roundNumber) {
      if (!roundNumber) return null;
      return `qualification_${roundNumber}`;
    },
    stageCodeForStage(stageId) {
      if (!stageId) return null;
      const stage = this.competition?.round_sizes?.find(s => s.id === stageId);
      if (stage?.code) return stage.code;
      if (stage?.athlete_count) return `round_${stage.athlete_count}`;
      return null;
    },
    calendarIdFromRound(round) {
      if (!round) return null;
      const candidates = [
        round?.stage_calendar_id,
        round?.calendar_stage_id,
        round?.calendar_id,
        round?.calendarId,
      ];
      const found = candidates.find(v => v !== undefined && v !== null);
      return found ?? null;
    },
    calendarIdFromRounds(rounds) {
      if (!Array.isArray(rounds)) return null;
      for (const round of rounds) {
        const id = this.calendarIdFromRound(round);
        if (id !== null && id !== undefined) return id;
      }
      return null;
    },
    findCalendarById(calendarId) {
      if (calendarId === null || calendarId === undefined) return null;
      return this.stageCalendars.find(c => Number(c.id) === Number(calendarId)) || null;
    },
    findCalendarByCode(code) {
      return this.stageCalendars.find(c => c.stage_code === code) || null;
    },
    findCalendarForRoundNumber(roundNumber) {
      if (!roundNumber) return null;
      const rounds = this.competitionRounds?.filter(
          r => r.round_size_id === null && r.round_number === roundNumber
      ) || [];

      const calendarId = this.calendarIdFromRounds(rounds);
      const byId = this.findCalendarById(calendarId);
      if (byId) return byId;

      const code = this.stageCodeForRound(roundNumber);
      if (code) {
        const byCode = this.findCalendarByCode(code);
        if (byCode) return byCode;
      }

      return null;
    },
    findCalendarForStage(stageId) {
      if (!stageId) return null;
      const stage = this.competition?.round_sizes?.find(s => s.id === stageId);

      const roundsForStage = this.competitionRounds?.filter(r => Number(r.round_size_id) === Number(stageId)) || [];
      const calendarId = this.calendarIdFromRounds(roundsForStage);
      const byRoundId = this.findCalendarById(calendarId);
      if (byRoundId) return byRoundId;

      // 1) match directo por round_size_id
      const byId = this.stageCalendars.find(c => Number(c.round_size_id) === Number(stageId));
      if (byId) return byId;

      // 2) match por stage_code del stage
      const stageCode = this.stageCodeForStage(stageId);
      if (stageCode) {
        const byCode = this.findCalendarByCode(stageCode);
        if (byCode) return byCode;
      }

      // 3) fallback por tamaño (round_{athlete_count})
      if (stage?.athlete_count) {
        const bySize = this.findCalendarByCode(`round_${stage.athlete_count}`);
        if (bySize) return bySize;
      }

      return null;
    },
    fallbackCalendarFromRounds(rounds) {
      if (!Array.isArray(rounds) || !rounds.length) return null;
      const pickField = (keys) => {
        for (const round of rounds) {
          for (const key of keys) {
            if (round && round[key]) return round[key];
            if (round?.stage_calendar && round.stage_calendar[key]) return round.stage_calendar[key];
          }
        }
        return null;
      };
      const opens = pickField([
        'upload_opens_at', 'upload_open_at', 'opens_at', 'open_at', 'opens',
        'uploadOpensAt', 'uploadOpenAt', 'opensAt', 'openAt',
      ]);
      const closes = pickField([
        'upload_closes_at', 'upload_close_at', 'closes_at', 'close_at', 'closes',
        'uploadClosesAt', 'uploadCloseAt', 'closesAt', 'closeAt',
      ]);
      const publish = pickField(['publish_at', 'published_at', 'publishAt', 'publishedAt']);
      if (!opens && !closes && !publish) return null;
      return { opens, closes, publish };
    },
    calendarTimes(calendar) {
      if (!calendar) return { opens: null, closes: null, publish: null };
      const opens = calendar.upload_opens_at
        || calendar.upload_open_at
        || calendar.uploadOpensAt
        || calendar.uploadOpenAt
        || calendar.opens_at
        || calendar.open_at
        || calendar.opensAt
        || calendar.openAt
        || calendar.opens
        || null;
      const closes = calendar.upload_closes_at
        || calendar.upload_close_at
        || calendar.uploadClosesAt
        || calendar.uploadCloseAt
        || calendar.closes_at
        || calendar.close_at
        || calendar.closesAt
        || calendar.closeAt
        || calendar.closes
        || null;
      const publish = calendar.publish_at
        || calendar.published_at
        || calendar.publishAt
        || calendar.publishedAt
        || null;
      return { opens, closes, publish };
    },
    pickVerificationCode(list) {
      if (!Array.isArray(list)) return null;
      const mine = list.find(item => this.isInnerAthlete(this.user, item) && item.verification_code);
      return mine?.verification_code || null;
    },
    mergeCalendarWithFallback(calendar, rounds) {
      const fallback = this.fallbackCalendarFromRounds(rounds);
      if (!calendar && !fallback) return null;
      if (!calendar) return fallback;
      if (!fallback) return calendar;

      const baseTimes = this.calendarTimes(calendar);
      const fbTimes = this.calendarTimes(fallback);

      const merged = { ...calendar };

      if (!baseTimes.opens && fbTimes.opens) {
        merged.upload_opens_at = fbTimes.opens;
      }
      if (!baseTimes.closes && fbTimes.closes) {
        merged.upload_closes_at = fbTimes.closes;
      }
      if (!baseTimes.publish && fbTimes.publish) {
        merged.publish_at = fbTimes.publish;
      }

      return merged;
    },
    scrollToSection() {
      this.$nextTick(() => {
        const el = this.$refs.sectionContainer;
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    },
    formatAdaptiveCountdown(ms) {
      if (ms === null) return '--';
      const clamped = Math.max(ms, 0);
      const totalSeconds = Math.floor(clamped / 1000);
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      const pad2 = (n) => String(n).padStart(2, '0');

      // Más de 24h: mostrar días + HH:MM
      if (days > 0) {
        return `${days}d ${pad2(hours)}:${pad2(minutes)}`;
      }

      // Entre 1h y 24h: HH:MM
      if (hours > 0) {
        return `${pad2(hours)}:${pad2(minutes)}`;
      }

      // Menos de 1h: MM:SS
      return `${pad2(minutes)}:${pad2(seconds)}`;
    }
  }
};
</script>
<style scoped>
.competition-rounds {
  margin-top: 20px;
}

.round-buttons, .stage-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 15px;
}

.round-toggle {
  border: 1px solid #dbe4f0;
  background: linear-gradient(180deg, #ffffff, #f8fafc);
  color: #0f172a;
  border-radius: 16px;
  cursor: pointer;
  min-width: 160px;
  min-height: 68px;
  padding: 10px 14px;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06);
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease, background 0.15s ease;
}

.round-toggle.active {
  background: linear-gradient(135deg, #0f172a, #1d4ed8);
  color: #fff;
  border-color: #1d4ed8;
  box-shadow: 0 16px 30px rgba(29, 78, 216, 0.18);
}

.round-toggle.active:hover {
  color: #fff;
}

.round-toggle:hover {
  transform: translateY(-1px);
  border-color: #93c5fd;
  box-shadow: 0 14px 28px rgba(37, 99, 235, 0.12);
}

.stage-tab-button {
  display: inline-flex;
  align-items: stretch;
  justify-content: flex-start;
  text-align: left;
}

.stage-tab-button__copy {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  min-width: 0;
}

.stage-tab-button__title {
  font-size: 0.95rem;
  font-weight: 800;
}

.stage-tab-button__meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.stage-tab-button__count,
.stage-tab-button__status {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 24px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 800;
}

.stage-tab-button__count {
  background: rgba(148, 163, 184, 0.18);
  color: inherit;
}

.stage-tab-button__status--finished {
  background: #dcfce7;
  color: #15803d;
}

.stage-tab-button__status--in_progress {
  background: #dbeafe;
  color: #1d4ed8;
}

.stage-tab-button__status--waiting {
  background: #e5e7eb;
  color: #4b5563;
}

.podium-toggle {
  margin-bottom: 18px;
}

.stage-tab-button--qualification {
  border-color: #bfdbfe;
}

.stage-tab-button--best {
  border-color: #86efac;
}

.stage-tab-button--stage {
  border-color: #fed7aa;
}

.stage-tab-button--podium {
  border-color: #ddd6fe;
}

.truncate-text {
  display: block;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 1rem;
}
.header-btn {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 65%;
  margin-bottom: 1rem;
}
.publish-action-group {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  max-width: 420px;
}
.advance-stage-btn {
  align-self: flex-start;
  flex: 0 0 auto;
}
.publish-block-reasons {
  margin: 0;
  padding-left: 18px;
  color: #92400e;
  font-size: 0.85rem;
}
.publish-block-reasons__item {
  line-height: 1.25;
}
.advance-btn {
  position: relative;
  transition: all 0.3s ease;
}

.icon-arrow {
  margin-left: 0.5rem;
  transition: transform 0.3s ease;
}

.advance-btn:hover .icon-arrow {
  transform: translateX(5px);
}
.action-btn {
  background-color: #007bff;
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}
.action-btn.view-rounds {
  color:#000;
  background-color: #ffc107;
}


.section-label {
  margin: 8px 0 6px;
  color: #6b7280;
  font-weight: 600;
  font-size: 0.95rem;
}

@media (max-width: 576px) {
  .round-toggle {
    width: 100%;
  }

  .header-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .header-btn {
    width: 100%;
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }

  .publish-action-group {
    max-width: 100%;
  }

  .header-btn .btn {
    width: 100%;
  }
}

/* Asegura que el botón de subida mantenga el estilo circular con progreso dentro de CompetitionTable */
:deep(.video-button) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 50%;
}
:deep(.progress-circle) {
  position: relative;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #e5e7eb;
  display: grid;
  place-items: center;
  color: #0f172a;
  font-size: 11px;
  font-weight: 700;
}
:deep(.progress-text) {
  position: relative;
  z-index: 1;
}
</style>
